import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
    PRINTER_KEYS,
    NO_PRINTER_ID,
    PAIR_ANOTHER_ID,
    getSelectedPrinters,
    setSelectedPrinter,
    clearSelectedPrinters,
    getPrinterList as getSavedPrinterList,
    savePrinterList as savePrinterListUtil,
    clearPrinterList as clearPrinterListUtil,
    ensureValidSelection,
} from '@utilities/printer';

// Actual Bluetooth scan function using Web Bluetooth API
async function scanForPrinters() {
    // Check if Web Bluetooth is supported
    if (!(window.navigator as any)?.bluetooth) {
        console.log('Web Bluetooth not supported, returning fallback printer');
        return [
            { id: "NO_PRINTER", name: "No Printer Available (Fallback)" }
        ];
    }

    const discoveredPrinters: { id: string; name: string }[] = [];

    try {
        // First, try to get already paired devices (this doesn't show a dialog)
        try {
            const pairedDevices = await (window.navigator as any).bluetooth.getDevices();
            pairedDevices.forEach((device: any) => {
                if (device.name && !discoveredPrinters.find(p => p.id === device.id || p.name === device.name)) {
                    discoveredPrinters.push({
                        id: device.id || device.name,
                        name: device.name
                    });
                }
            });
            console.log(`Found ${pairedDevices.length} paired devices`);
        } catch (error) {
            console.log('Could not get paired devices:', error);
        }

        // Add option to pair another printer
        discoveredPrinters.push({
            id: "PAIR_ANOTHER_PRINTER",
            name: "➕ Pair Another Printer..."
        });

        // If we have paired devices, return them with the pair option
        if (discoveredPrinters.length > 1) { // More than just the "Pair Another" option
            console.log(`Returning ${discoveredPrinters.length - 1} paired devices plus pair option`);
            return discoveredPrinters;
        }

        // If no paired devices, try to pair the first one
        try {
            const device = await (window.navigator as any).bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [
                    '000018f0-0000-1000-8000-00805f9b34fb', // Common thermal printer service UUID
                    '00001800-0000-1000-8000-00805f9b34fb', // Generic Access service
                    '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute service
                    '0000180f-0000-1000-8000-00805f9b34fb', // Battery service (common in printers)
                    '0000180a-0000-1000-8000-00805f9b34fb'  // Device Information service
                ]
            });

            // Add the discovered device to our list
            if (device && device.name) {
                // Check if device is already in the list (by ID or name)
                const existingDevice = discoveredPrinters.find(p => 
                    p.id === device.id || 
                    p.name === device.name || 
                    (device.id && p.id === device.id)
                );
                if (!existingDevice) {
                    discoveredPrinters.unshift({ // Add at beginning, before "Pair Another" option
                        id: device.id || device.name,
                        name: device.name
                    });
                    console.log(`Added newly paired device: ${device.name}`);
                } else {
                    console.log(`Device ${device.name} already exists in list, skipping duplicate`);
                }
            }
        } catch (error) {
            console.log('User cancelled initial pairing or pairing failed:', error);
        }

        // If still no devices found, add fallback printer
        if (discoveredPrinters.length === 1) { // Only "Pair Another" option
            console.log('No Bluetooth printers found, adding fallback printer');
            discoveredPrinters.unshift({
                id: "NO_PRINTER",
                name: "No Printer Available (Fallback)"
            });
        }

        console.log(`Total discovered printers: ${discoveredPrinters.length}`);
        return discoveredPrinters;

    } catch (error) {
        console.error('Bluetooth scanning error:', error);
        
        // For any error, return fallback printer with pair option
        console.log('Bluetooth scanning failed, returning fallback printer');
        return [
            { id: "NO_PRINTER", name: "No Printer Available (Fallback)" },
            { id: "PAIR_ANOTHER_PRINTER", name: "➕ Pair Another Printer..." }
        ];
    }
}

// Function to handle pairing additional printers
async function pairAdditionalPrinter() {
    if (!(window.navigator as any)?.bluetooth) {
        throw new Error('Web Bluetooth not supported');
    }

    try {
        const device = await (window.navigator as any).bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: [
                '000018f0-0000-1000-8000-00805f9b34fb', // Common thermal printer service UUID
                '00001800-0000-1000-8000-00805f9b34fb', // Generic Access service
                '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute service
                '0000180f-0000-1000-8000-00805f9b34fb', // Battery service (common in printers)
                '0000180a-0000-1000-8000-00805f9b34fb'  // Device Information service
            ]
        });

        if (device && device.name) {
            return {
                id: device.id || device.name,
                name: device.name
            };
        }
    } catch (error) {
        console.error('Pairing additional printer failed:', error);
        throw error;
    }
}

// Helper function to remove duplicates from printer list
function removeDuplicatePrinters(printers: { id: string; name: string }[]) {
    const uniquePrinters: { id: string; name: string }[] = [];
    const seenIds = new Set<string>();
    const seenNames = new Set<string>();

    printers.forEach(printer => {
        // Skip if we've already seen this ID or name (except for special cases)
        if (printer.id === "PAIR_ANOTHER_PRINTER" || printer.id === "NO_PRINTER") {
            // For special cases, only add once
            if (!seenIds.has(printer.id)) {
                uniquePrinters.push(printer);
                seenIds.add(printer.id);
            }
        } else {
            // For regular printers, check both ID and name
            if (!seenIds.has(printer.id) && !seenNames.has(printer.name)) {
                uniquePrinters.push(printer);
                seenIds.add(printer.id);
                seenNames.add(printer.name);
            }
        }
    });

    return uniquePrinters;
}

const _PRINTER_STORAGE_KEY = PRINTER_KEYS.PRINTER_STORAGE_KEY;
const _PRINTER_LIST_STORAGE_KEY = PRINTER_KEYS.PRINTER_LIST_STORAGE_KEY;

export default function KOTPrinterSettings() {
    const [printers, setPrinters] = useState<{ id: string; name: string }[]>([]);
    const [selected, setSelected] = useState<{ main?: string; kitchen?: string }>({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
    const [removeDialog, setRemoveDialog] = useState<{ open: boolean; printerId: string; printerName: string; type: "main" | "kitchen" } | null>(null);
    const [clearDialog, setClearDialog] = useState(false);

    useEffect(() => {
        // Load saved printer configuration and printer list
        loadSavedData();
    }, []);

    const loadSavedData = () => {
        try {
            // Load selected printer configuration
            const config = getSelectedPrinters();
            if (config) {
                setSelected(config);
            }

            // Load saved printer list
            const printerList = getSavedPrinterList();
            if (printerList) {
                setPrinters(printerList);
                console.log(`Loaded ${printerList.length} saved printers from localStorage`);
            }
        } catch (error) {
            console.error('Error loading saved data:', error);
        }
    };

    const savePrintersToStorage = (printerList: { id: string; name: string }[]) => {
        try {
            savePrinterListUtil(printerList);
            console.log(`Saved ${printerList.length} printers to localStorage`);
        } catch (error) {
            console.error('Error saving printers to localStorage:', error);
        }
    };

    const handleScan = async () => {
        setLoading(true);
        setMessage(null);
        try {
            const found = await scanForPrinters();
            const uniquePrinters = removeDuplicatePrinters(found);
            setPrinters(uniquePrinters);
            savePrintersToStorage(uniquePrinters); // Save the full list
            setMessage({ type: 'success', text: `Found ${uniquePrinters.length} unique printers.` });
        } catch (error) {
            console.error('Error scanning printers:', error);
            setMessage({ type: 'error', text: 'Failed to scan for printers.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (type: "main" | "kitchen", printerId: string) => {
        setLoading(true);
        try {
            // Handle special case for pairing additional printer
            if (printerId === PAIR_ANOTHER_ID) {
                try {
                    const newPrinter = await pairAdditionalPrinter();
                    if (newPrinter) {
                        // Add the newly paired printer to the list with deduplication
                        setPrinters(prev => {
                            const filtered = prev.filter(p => p.id !== PAIR_ANOTHER_ID);
                            const withNewPrinter = [...filtered, newPrinter, { id: PAIR_ANOTHER_ID, name: "➕ Pair Another Printer..." }];
                            const uniquePrinters = removeDuplicatePrinters(withNewPrinter);
                            savePrintersToStorage(uniquePrinters); // Save the updated list
                            return uniquePrinters;
                        });
                        
                        setMessage({ type: 'success', text: `Successfully paired ${newPrinter.name}! You can now select it as your ${type === 'main' ? 'main billing' : 'kitchen'} printer.` });
                    }
                } catch (error) {
                    console.error('Pairing failed:', error);
                    if (error instanceof Error && error.message.includes('User cancelled')) {
                        setMessage({ type: 'info', text: 'Printer pairing was cancelled.' });
                    } else {
                        setMessage({ type: 'error', text: 'Failed to pair new printer. Please try again.' });
                    }
                }
                return;
            }

            // Normal printer selection - ensure only one printer per type
            if (!ensureValidSelection(printerId)) {
                setMessage({ type: 'error', text: 'Please select a valid printer.' });
                return;
            }
            const newSelected = { ...selected, [type]: printerId };
            setSelected(newSelected);
            setSelectedPrinter(type, printerId);
            setMessage({ type: 'success', text: `${type === 'main' ? 'Main billing' : 'Kitchen'} printer saved to this device!` });
        } catch (error) {
            console.error('Error saving printer:', error);
            setMessage({ type: 'error', text: 'Failed to save printer configuration.' });
        } finally {
            setLoading(false);
        }
    };

    const handlePairNewForType = async (type: "main" | "kitchen") => {
        setLoading(true);
        try {
            const newPrinter = await pairAdditionalPrinter();
            if (newPrinter) {
                // Add the newly paired printer to the list with deduplication
                setPrinters(prev => {
                    const filtered = prev.filter(p => p.id !== "PAIR_ANOTHER_PRINTER");
                    const withNewPrinter = [...filtered, newPrinter, { id: "PAIR_ANOTHER_PRINTER", name: "➕ Pair Another Printer..." }];
                    const uniquePrinters = removeDuplicatePrinters(withNewPrinter);
                    savePrintersToStorage(uniquePrinters); // Save the updated list
                    return uniquePrinters;
                });
                
                // Automatically select the newly paired printer for this type
                const newSelected = { ...selected, [type]: newPrinter.id };
                setSelected(newSelected);
                setSelectedPrinter(type, newPrinter.id);
                
                setMessage({ type: 'success', text: `Successfully paired and selected ${newPrinter.name} as ${type === 'main' ? 'main billing' : 'kitchen'} printer!` });
            }
        } catch (error) {
            console.error('Pairing failed:', error);
            if (error instanceof Error && error.message.includes('User cancelled')) {
                setMessage({ type: 'info', text: 'Printer pairing was cancelled.' });
            } else {
                setMessage({ type: 'error', text: 'Failed to pair new printer. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemovePrinter = (type: "main" | "kitchen", printerId: string) => {
        const printer = printers.find(p => p.id === printerId);
        if (printer) {
            setRemoveDialog({
                open: true,
                printerId,
                printerName: printer.name,
                type
            });
        }
    };

    const confirmRemovePrinter = () => {
        if (removeDialog) {
            const { printerId, type } = removeDialog;
            setPrinters(prev => {
                const updatedPrinters = prev.filter(p => p.id !== printerId);
                savePrintersToStorage(updatedPrinters); // Save the updated list
                // If the removed printer was selected, clear the selection for that type
                if (selected[type] === printerId) {
                    const newSelected = { ...selected, [type]: undefined };
                    setSelected(newSelected);
                    setSelectedPrinter(type, undefined);
                    setMessage({ type: 'success', text: `${removeDialog.printerName} removed and selection cleared.` });
                } else {
                    setMessage({ type: 'success', text: `${removeDialog.printerName} removed from list.` });
                }
                return updatedPrinters;
            });
            setRemoveDialog(null);
        }
    };

    const handleClearAllPrinters = () => {
        setClearDialog(true);
    };

    const confirmClearAllPrinters = () => {
        setPrinters([]);
        setSelected({});
        clearSelectedPrinters();
        clearPrinterListUtil();
        setMessage({ type: 'success', text: 'All printer configurations cleared.' });
        setClearDialog(false);
    };

    const renderPrinterList = (type: "main" | "kitchen") => {
        const selectedPrinter = selected[type];
        const typeLabel = type === 'main' ? 'Main Billing' : 'Kitchen';
        
        return (
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3">
                            {typeLabel} Printer
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePairNewForType(type)}
                            disabled={loading}
                            startIcon={<AddIcon />}
                        >
                            Pair New
                        </Button>
                    </Box>
                    
                    {printers.length === 0 ? (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No printers available. Click "Scan for Printers" to discover devices.
                        </Typography>
                    ) : (
                        <List dense>
                            {printers.map((printer) => {
                                const isSelected = selectedPrinter === printer.id;
                                const isPairOption = printer.id === PAIR_ANOTHER_ID;
                                
                                return (
                                    <ListItem
                                        key={printer.id}
                                        sx={{
                                            border: isSelected ? 2 : 1,
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            backgroundColor: isSelected ? 'primary.light' : 'background.paper',
                                            '&:hover': {
                                                backgroundColor: isSelected ? 'primary.light' : 'action.hover',
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isSelected && <CheckCircleIcon color="primary" />}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: isSelected ? 'bold' : 'normal',
                                                            color: isSelected ? 'primary.main' : 'text.primary'
                                                        }}
                                                    >
                                                        {printer.name}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                isSelected ? 
                                                    `Selected as ${typeLabel} printer` : 
                                                    isPairOption ? 
                                                        'Click to pair a new printer' : 
                                                        'Click to select'
                                            }
                                        />
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {!isPairOption && !isSelected && printer.id !== NO_PRINTER_ID && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleSave(type, printer.id)}
                                                    disabled={loading}
                                                >
                                                    Select
                                                </Button>
                                            )}
                                            {isSelected && (
                                                <Chip
                                                    label="Selected"
                                                    color="primary"
                                                    size="small"
                                                    icon={<CheckCircleIcon />}
                                                />
                                            )}
                                            {isPairOption && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleSave(type, printer.id)}
                                                    disabled={loading}
                                                    startIcon={<AddIcon />}
                                                >
                                                    Pair
                                                </Button>
                                            )}
                                            {isSelected && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemovePrinter(type, printer.id)}
                                                    disabled={loading}
                                                    color="error"
                                                    title="Remove printer"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
                KOT Printer Configuration
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure your main billing and kitchen printers for KOT printing. 
                You can pair multiple printers and select which one to use for each type.
            </Typography>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }}>
                    {message.text}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleScan}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <BluetoothSearchingIcon />}
                >
                    {loading ? 'Scanning...' : 'Scan for Printers'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleClearAllPrinters}
                    disabled={loading}
                    startIcon={<ClearAllIcon />}
                    sx={{ ml: 1 }}
                >
                    Clear All
                </Button>
            </Box>

            {renderPrinterList("main")}
            {renderPrinterList("kitchen")}

            {printers.length > 0 && (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Current Configuration
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`Main: ${selected.main ? printers.find(p => p.id === selected.main)?.name || 'Not set' : 'Not set'}`}
                                color={selected.main ? 'primary' : 'default'}
                                variant={selected.main ? 'filled' : 'outlined'}
                            />
                            <Chip
                                label={`Kitchen: ${selected.kitchen ? printers.find(p => p.id === selected.kitchen)?.name || 'Not set' : 'Not set'}`}
                                color={selected.kitchen ? 'primary' : 'default'}
                                variant={selected.kitchen ? 'filled' : 'outlined'}
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Remove Printer Confirmation Dialog */}
            <Dialog
                open={removeDialog?.open || false}
                onClose={() => setRemoveDialog(null)}
                aria-labelledby="remove-printer-dialog-title"
            >
                <DialogTitle id="remove-printer-dialog-title">
                    Remove Printer
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove "{removeDialog?.printerName}" from your printer list?
                        {selected[removeDialog?.type || 'main'] === removeDialog?.printerId && 
                            " This printer is currently selected and will be deselected."
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveDialog(null)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmRemovePrinter} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear All Printers Confirmation Dialog */}
            <Dialog
                open={clearDialog}
                onClose={() => setClearDialog(false)}
                aria-labelledby="clear-all-dialog-title"
            >
                <DialogTitle id="clear-all-dialog-title">
                    Clear All Printers
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove all printers from your list? 
                        This will also clear your current printer selections for both main billing and kitchen.
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearDialog(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmClearAllPrinters} color="error" variant="contained">
                        Clear All
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
} 